import { createContext } from "react";

import type { Permission } from "src/api/Tmd.dtos";
import type { AppIconDefinition } from "src/components/common/Icon";

/**
 * Selects whether link permission requirements use all or any matching grants.
 */
export enum LinkPermissionMode {
    All = 1,
    Any = 2
}

/**
 * Selects the layout's exact-permission or any-permission interpretation.
 */
export enum LayoutPermissionMode {
    Exact = 1,
    Any = 2
}

/**
 * Selects contextual or unrestricted-scope permission matching for client presentation.
 */
export enum PermissionScopeMode {
    Contextual = 1,
    Any = 2
}

/**
 * Carries optional entity IDs used by layout, permission and notice consumers.
 */
export interface ILayoutItemContext {
    trialId?: number;
    institutionId?: number;
    trialInstitutionId?: number;
    departmentId?: number;
    patientId?: number;
    groupId?: number;
    personnelId?: number;
}

/**
 * Describes navigation or an action with optional display and client permission requirements.
 */
export interface ILayoutLink {
    icon?: AppIconDefinition;
    label?: string;
    href: string;
    actionFn?: () => void;
    newTab?: boolean;
    permissions?: Permission[];
    permissionMode?: LinkPermissionMode;
    permissionScopeMode?: PermissionScopeMode;
    className?: string;
    style?: React.CSSProperties;
}

/**
 * Groups coded navigation items and their optional entity context.
 */
export interface ILayoutSubMenu {
    code: string;
    title?: string;
    context?: ILayoutItemContext;
    items: ILayoutLink[];
}

/**
 * Describes an ordered breadcrumb and its identity icon/loading presentation.
 */
export interface ILayoutBreadcrumb {
    code: string;
    label?: string;
    href?: string;
    icon: AppIconDefinition;
    iconStyle?: "single" | "collection";
    order: number;
    loading?: boolean;
}

/**
 * Contains the current navigation, page permission settings and entity context.
 */
export interface ILayoutContext {
    menuItems?: ILayoutLink[];
    subMenus?: ILayoutSubMenu[];
    restricted?: boolean;
    permission?: Permission;
    permissionMode?: LayoutPermissionMode;
    permissionScopeMode?: PermissionScopeMode;
    context: ILayoutItemContext;
    loading?: boolean;
    breadcrumbs?: ILayoutBreadcrumb[];
}

/**
 * Describes layout update requests; provider actions schedule changes for a later render.
 */
export interface ILayoutActionsContext {
    setMenuItems: (menuItems: ILayoutLink[]) => void;
    setSubMenus: (subMenus: ILayoutSubMenu[]) => void;
    addSubMenu: (subMenu: ILayoutSubMenu) => void;
    removeSubMenu: (subMenu: ILayoutSubMenu) => void;
    addSubMenuByCode: (code: string) => void;
    removeSubMenuByCode: (code: string) => void;
    setRestricted: (restricted: boolean) => void;
    setPermission: (permission: Permission) => void;
    setPermissionMode: (permissionMode: LayoutPermissionMode) => void;
    setPermissionScopeMode: (permissionScopeMode: PermissionScopeMode) => void;
    setLoading: (loading: boolean) => void;
    setContext: (context: ILayoutItemContext) => void;
    setPageBreadcrumbs: (owner: string, breadcrumbs: ILayoutBreadcrumb[]) => void;
    removePageBreadcrumbs: (owner: string) => void;
}

/**
 * Provides inert actions outside a layout provider; calls do not update layout state.
 */
export const defaultLayoutActionsContext: ILayoutActionsContext = {
    setMenuItems: (menuItems: ILayoutLink[]) => {},
    setSubMenus: (subMenus: ILayoutSubMenu[]) => {},
    addSubMenu: (subMenu: ILayoutSubMenu) => {},
    removeSubMenu: (subMenu: ILayoutSubMenu) => {},
    addSubMenuByCode: (code: string) => {},
    removeSubMenuByCode: (code: string) => {},
    setRestricted: (restricted: boolean) => {},
    setPermission: (permission: Permission) => {},
    setPermissionMode: (permissionMode: LayoutPermissionMode) => {},
    setPermissionScopeMode: (permissionScopeMode: PermissionScopeMode) => {},
    setLoading: (loading: boolean) => {},
    setContext: (context: ILayoutItemContext) => {},
    setPageBreadcrumbs: (owner: string, breadcrumbs: ILayoutBreadcrumb[]) => {},
    removePageBreadcrumbs: (owner: string) => {}
};

/**
 * Defines empty navigation and context with exact, contextual permission defaults.
 */
export const defaultLayoutContext: ILayoutContext = {
    menuItems: [],
    subMenus: [],
    restricted: false,
    permission: undefined,
    permissionMode: LayoutPermissionMode.Exact,
    permissionScopeMode: PermissionScopeMode.Contextual,
    loading: false,
    breadcrumbs: [],
    context: {}
};

/**
 * Shares the current layout snapshot, using defaultLayoutContext without a provider.
 */
export const LayoutContext = createContext<ILayoutContext>(defaultLayoutContext);

/**
 * Shares layout dispatch actions, using inert defaults without a provider.
 */
export const LayoutActionsContext = createContext<ILayoutActionsContext>(
    defaultLayoutActionsContext
);

export default LayoutContext;
